import { useEffect, useRef, useState, type ReactNode } from "react";
import { createPortal } from "react-dom";
import { Bell, X } from "lucide-react";
import { api } from "./api";
import { PersonalUpdates } from "./UpdatesPanel";
import "./changes.css";
import "./updates-hub.css";
export type BoardChangesView = {
  content: ReactNode;
  unread: number;
  name: string;
  reviewing: boolean;
  show: () => void;
  hide: () => void;
};
export function UpdatesButton({
  changes,
  notificationsEnabled = true,
}: {
  changes?: BoardChangesView;
  notificationsEnabled?: boolean;
}) {
  const [open, setOpen] = useState(false),
    [view, setView] = useState<"for-you" | "changes">("for-you");
  const [unread, setUnread] = useState(0);
  const trigger = useRef<HTMLButtonElement>(null),
    closeButton = useRef<HTMLButtonElement>(null);
  const latest = useRef(changes);
  latest.current = changes;
  const close = () => {
    setOpen(false);
    latest.current?.hide();
    trigger.current?.focus();
  };
  useEffect(() => {
    if (!notificationsEnabled) return;
    let live = true;
    const refresh = async () => {
      if (document.hidden) return;
      try {
        const result = await api<{ unread: number }>("/api/updates/count");
        if (live) setUnread(result.unread);
      } catch {
        if (live) setUnread(0);
      }
    };
    void refresh();
    const timer = setInterval(refresh, 15000);
    window.addEventListener("planroom:updates-read", refresh);
    document.addEventListener("visibilitychange", refresh);
    return () => {
      live = false;
      clearInterval(timer);
      window.removeEventListener("planroom:updates-read", refresh);
      document.removeEventListener("visibilitychange", refresh);
    };
  }, [notificationsEnabled]);
  useEffect(() => {
    if (open) closeButton.current?.focus();
  }, [open]);
  useEffect(() => {
    const hide = () => {
      setOpen(false);
      latest.current?.hide();
    };
    window.addEventListener("planroom:close-updates", hide);
    return () => window.removeEventListener("planroom:close-updates", hide);
  }, []);
  const switchView = (next: typeof view) => {
    setView(next);
    if (next === "changes") changes?.show();
    else changes?.hide();
  };
  return (
    <>
      <button
        ref={trigger}
        className="header-icon-button updates-trigger"
        title="Updates"
        aria-label={
          unread ? `Updates, ${unread} unread notifications` : "Updates"
        }
        aria-expanded={open}
        aria-haspopup="dialog"
        onClick={() => {
          if (open) close();
          else {
            setOpen(true);
            if (view === "changes") changes?.show();
          }
        }}
      >
        <Bell size={18} />
        {unread > 0 && (
          <span className="updates-count">{unread > 99 ? "99+" : unread}</span>
        )}
      </button>
      {open &&
        createPortal(
          <section
            className={`changes-panel updates-hub${view === "changes" && changes?.reviewing ? " reviewing" : ""}`}
            role="dialog"
            aria-label="Updates"
            onKeyDown={(e) => {
              e.stopPropagation();
              if (e.key === "Escape") close();
            }}
          >
            <header>
              <div>
                <strong>Updates</strong>
                <small>
                  {view === "changes"
                    ? changes?.name
                    : "Mentions, replies & actions"}
                </small>
              </div>
              <button
                ref={closeButton}
                onClick={close}
                aria-label="Close Updates"
              >
                <X size={18} />
              </button>
            </header>
            {changes && (
              <nav aria-label="Update views">
                <button
                  aria-pressed={view === "for-you"}
                  onClick={() => switchView("for-you")}
                >
                  For you
                  {unread > 0 && (
                    <span className="updates-tab-count">{unread}</span>
                  )}
                </button>
                <button
                  aria-pressed={view === "changes"}
                  onClick={() => switchView("changes")}
                >
                  Changes
                  {changes.unread > 0 && (
                    <span
                      className="updates-new-dot"
                      role="img"
                      aria-label="New project changes"
                    />
                  )}
                </button>
              </nav>
            )}
            {view === "changes" && changes ? (
              changes.content
            ) : (
              <PersonalUpdates close={close} enabled={notificationsEnabled} />
            )}
          </section>,
          document.body,
        )}
    </>
  );
}
